/**
 * Alle Felder heissen genauso wie die Spalten in der Datenbank: snake_case,
 * von der Spalte ueber das JSON bis in die React-Komponente. Unterwegs wird
 * nichts umbenannt.
 */

export type review_status = 'open' | 'submitted' | 'accepted' | 'rejected'

export interface receipt {
  id: number
  payment_id: number
  file_name: string
  byte_size: number
  uploaded_at: string
}

export interface rejection_note {
  id: number
  payment_id: number
  note: string
  created_at: string
}

export interface payment {
  id: number
  user_id: number
  import_run_id: number
  booking_date: string
  value_date: string | null
  counterparty_name: string
  purpose: string
  iban: string | null
  bank: string | null
  amount: string
  receipt_required: boolean
  review_status: review_status
  reviewed_at: string | null
  receipts: receipt[]
  rejection_notes: rejection_note[]
}

export interface pending_payment extends payment {
  user_name: string
}

export interface user {
  id: number
  name: string
  email: string
  reminder_interval_days: number
  reminders_paused: boolean
  last_reminder_sent_at: string | null
  created_at: string
}

export interface user_overview extends user {
  payment_count: number
  required_count: number
  reminderable_count: number
  pending_review_count: number
}

export interface import_run {
  id: number
  user_id: number
  file_name: string
  total_rows: number
  inserted_rows: number
  skipped_duplicates: number
  created_at: string
  has_receipts: boolean
}

export interface import_preview {
  total_rows: number
  new_rows: number
  skipped_duplicates: number
  entries: {
    booking_date: string
    counterparty_name: string
    purpose: string
    iban: string | null
    amount: string
    is_duplicate: boolean
  }[]
}

export interface portal_overview {
  name: string
  email: string
  max_receipts_per_payment: number
  max_upload_bytes: number
  payments: payment[]
}

export interface audit_entry {
  id: number
  occurred_at: string
  actor_type: 'admin' | 'user' | 'system'
  actor_label: string
  user_id: number | null
  action: string
  subject_type: string | null
  subject_id: number | null
  detail: Record<string, unknown>
}

export class api_error extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: 'same-origin', ...init })

  if (!response.ok) {
    let message = `Fehler ${response.status}`
    try {
      const body = (await response.json()) as { message?: string | string[] }
      if (body.message) message = Array.isArray(body.message) ? body.message.join('; ') : body.message
    } catch {
      // Antwort ohne JSON-Koerper — die Statusmeldung muss reichen.
    }
    throw new api_error(response.status, message)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

function json(method: string, body?: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  }
}

export const api = {
  admin_login: (username: string, password: string) =>
    request<{ ok: true }>('/api/auth/admin/login', json('POST', { username, password })),
  admin_logout: () => request<{ ok: true }>('/api/auth/admin/logout', json('POST')),
  admin_me: () => request<{ username: string }>('/api/auth/admin/me'),

  list_users: () => request<user_overview[]>('/api/admin/users'),
  get_user: (user_id: number) => request<user>(`/api/admin/users/${user_id}`),
  create_user: (name: string, email: string) =>
    request<user>('/api/admin/users', json('POST', { name, email })),
  update_user: (
    user_id: number,
    changes: Partial<Pick<user, 'name' | 'email' | 'reminder_interval_days' | 'reminders_paused'>>,
  ) => request<user>(`/api/admin/users/${user_id}`, json('PATCH', changes)),
  delete_user: (user_id: number) =>
    request<{ ok: true }>(`/api/admin/users/${user_id}`, json('DELETE')),
  send_reminder: (user_id: number) =>
    request<{ ok: true }>(`/api/admin/users/${user_id}/reminders/send`, json('POST')),

  list_payments: (user_id: number) => request<payment[]>(`/api/admin/users/${user_id}/payments`),
  pending_payments: () => request<pending_payment[]>('/api/admin/payments/pending'),
  get_payment: (payment_id: number) => request<payment>(`/api/admin/payments/${payment_id}`),
  set_receipt_required: (payment_id: number, receipt_required: boolean) =>
    request<payment>(
      `/api/admin/payments/${payment_id}/receipt-required`,
      json('PATCH', { receipt_required }),
    ),
  accept_payment: (payment_id: number) =>
    request<payment>(`/api/admin/payments/${payment_id}/accept`, json('POST')),
  reject_payment: (payment_id: number, note: string) =>
    request<payment>(`/api/admin/payments/${payment_id}/reject`, json('POST', { note })),
  unaccept_payment: (payment_id: number) =>
    request<payment>(`/api/admin/payments/${payment_id}/unaccept`, json('POST')),

  list_imports: (user_id: number) => request<import_run[]>(`/api/admin/users/${user_id}/imports`),
  preview_import: (user_id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<import_preview>(`/api/admin/users/${user_id}/imports/preview`, {
      method: 'POST',
      body: form,
    })
  },
  commit_import: (user_id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<{ import_run_id: number; inserted_rows: number; skipped_duplicates: number }>(
      `/api/admin/users/${user_id}/imports`,
      { method: 'POST', body: form },
    )
  },
  revert_import: (user_id: number, import_run_id: number) =>
    request<{ ok: true }>(`/api/admin/users/${user_id}/imports/${import_run_id}`, json('DELETE')),

  // Belege gehen nicht durch `request` — der Browser holt die Bytes selbst,
  // einmal zur Anzeige im iframe und einmal zum Speichern.
  receipt_file_url: (receipt_id: number) => `/api/admin/receipts/${receipt_id}/file`,
  receipt_download_url: (receipt_id: number) =>
    `/api/admin/receipts/${receipt_id}/file?download=1`,

  list_audit: () => request<audit_entry[]>('/api/admin/audit'),

  portal_overview: () => request<portal_overview>('/api/portal/overview'),
  upload_receipt: (payment_id: number, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<receipt>(`/api/portal/payments/${payment_id}/receipts`, {
      method: 'POST',
      body: form,
    })
  },
  delete_receipt: (receipt_id: number) =>
    request<{ ok: true }>(`/api/portal/receipts/${receipt_id}`, json('DELETE')),
}
